import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { Alert } from '../../../components/alert/Alert';
import { Button } from '../../../components/button/Button';
import { OrderSelfBillReviewFlag } from 'src/types/enums';
import { getDisplayError } from '../../../utils/get-display-error';

interface Props {
  orderId: string;
  flags: OrderSelfBillReviewFlag[];
  onChange?: () => void | Promise<void>;
}

const FLAG_LABEL: Record<OrderSelfBillReviewFlag, string> = {
  [OrderSelfBillReviewFlag.VatMismatch]: 'BTW-percentage wijkt af van standaard van vervoerder',
  [OrderSelfBillReviewFlag.TotalDiverged]: 'Totaal op het concept wijkt af van de actuele lijntotalen',
};

/**
 * Inline banner shown on the order detail when one or more
 * `selfBillReviewFlags` are set. The order is excluded from the self-billing
 * cron until the planner clears the flags here (or applies the suggested
 * VAT auto-correction).
 */
export const OrderSelfBillReviewBanner: React.FC<Props> = ({ orderId, flags: flagsFromOrder, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [flags, setFlags] = useState(flagsFromOrder);

  useEffect(() => {
    setFlags(flagsFromOrder);
  }, [flagsFromOrder]);

  if (!flags.length) return null;

  const post = async (action: 'clear' | 'apply-vat-correction') => {
    setBusy(true);
    try {
      const res = await fetch(`/api/v1/orders/${orderId}/self-bill-review/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Request failed (${res.status})`);
      }
      const body = (await res.json()) as { flags?: OrderSelfBillReviewFlag[] };
      if (body.flags) {
        setFlags(body.flags);
      } else if (action === 'clear') {
        setFlags([]);
      }
      toast.success(action === 'clear' ? 'Review-flags gewist' : 'BTW-correctie toegepast');
      await onChange?.();
    } catch (err) {
      captureException(err);
      toast.error(`Actie mislukt: ${getDisplayError(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const showVatCorrection = flags.includes(OrderSelfBillReviewFlag.VatMismatch);

  return (
    <Alert type="warning" className="my-2">
      <div className="flex flex-col gap-2">
        <div>
          <strong>Tegengehouden voor review:</strong>
          <ul className="list-disc ml-5 mt-1">
            {flags.map((f) => (
              <li key={f}>{FLAG_LABEL[f]}</li>
            ))}
          </ul>
        </div>
        <div className="flex gap-2 flex-wrap">
          {showVatCorrection && (
            <Button color="primary" isLoading={busy} onTrigger={() => post('apply-vat-correction')}>
              Pas BTW-correctie toe
            </Button>
          )}
          <Button color="secondary" isLoading={busy} onTrigger={() => post('clear')}>
            Markeer als gereviewd
          </Button>
        </div>
      </div>
    </Alert>
  );
};
